import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Key, Mail, Lock, Copy, CreditCard, LogOut, Monitor, Download, RefreshCw,
  Package, MessageCircle, XCircle, ShieldCheck, Unplug, Send, Sparkles, CheckCircle2,
  Layers
} from "lucide-react";
import axios from "axios";
import { RenewPlanDialog, AddSeatsDialog } from "./ClientPortalSubscriptionDialogs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage keys - namespaced to avoid conflicts with admin panel
const CLIENT_TOKEN_KEY = "client_auth_token";
const CLIENT_DATA_KEY = "client_user_data";
const WHATSAPP_LINK = "https://wa.me/917223863486";

/* ---------- Helpers (pure functions) ---------- */
const formatINR = (paise) => {
  if (!paise && paise !== 0) return "—";
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const daysRemaining = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24)));
};

const isActive = (lic) => {
  if (!lic) return false;
  return lic.status === "active" && new Date(lic.expiry_date) > new Date();
};

const cycleLabel = (cycle) => {
  const c = (cycle || "").toLowerCase();
  if (c === "monthly") return "Monthly";
  if (c === "quarterly") return "Quarterly";
  if (c === "yearly") return "Yearly";
  return cycle || "—";
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    toast.success("Copied to clipboard");
  } catch (err) {
    toast.error("Failed to copy");
  }
};

/* ---------- Product Card (reusable) ----------
 * One card renders a single product (grouped by display_group).
 * For a group with multiple variants (e.g. ORB Quarterly / Yearly), the
 * user can switch between variants via a segmented control at the top of
 * the card. Every variant is fully interactive — download, disconnect,
 * TradingView submit, renew, cancel — using its own license/product config.
 */
function ProductCard({ group, onDisconnect, onDownload, onDownloadNt, onSubmitTv, onRenew, onCancel, onAddSeats, downloadingId, ntDownloadingId, tvSubmittingId, cancellingId, renewingId }) {
  const { primary, variants } = group;
  const allEntries = [primary, ...(variants || [])];
  const [activeIdx, setActiveIdx] = useState(0);
  const activeEntry = allEntries[activeIdx] || primary;

  const lic = activeEntry.license;
  const prod = activeEntry.product;
  const sub = activeEntry.subscription || {};
  const active = isActive(lic);
  const [tvInput, setTvInput] = useState(lic.tv_username || "");
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // When the active variant changes, resync the TV input to that license's
  // stored username (so switching tabs doesn't leak text between variants).
  useEffect(() => {
    setTvInput(lic.tv_username || "");
  }, [lic.tv_username, lic.id]);

  const statusBadge = () => {
    if (lic.status === "active" && active)
      return <Badge className="bg-green-100 text-green-800 border-green-300">ACTIVE</Badge>;
    if (lic.status === "expired" || !active)
      return <Badge className="bg-red-100 text-red-800 border-red-300">EXPIRED</Badge>;
    return <Badge className="bg-gray-100 text-gray-800 border-gray-300">{(lic.status || "—").toUpperCase()}</Badge>;
  };

  const days = daysRemaining(lic.expiry_date);
  const accounts = lic.registered_accounts || [];
  const productDisplayName = prod.name || prod.product_id || "Product";
  // Strip cycle suffix like "(Quarterly)" from the name for group header
  const groupTitle = productDisplayName.replace(/\s*\([^)]+\)\s*$/, "");
  // Per-license MT5 and NT caps — use the customer's purchased entitlement
  // when set, else fall back to legacy defaults (2 accounts / product-level
  // nt_max_devices). The server is still the source of truth; these values
  // only drive on-screen labels.
  const mt5Cap = (typeof lic.mt5_max_activations === "number" && lic.mt5_max_activations > 0)
    ? lic.mt5_max_activations
    : 2;
  const ntCap = (typeof lic.nt_max_activations === "number" && lic.nt_max_activations > 0)
    ? lic.nt_max_activations
    : (prod.nt_max_devices || 2);

  return (
    <Card className="bg-white border border-gray-200 overflow-hidden" data-testid={`product-card-${prod.product_id}`}>
      {prod.banner_url && (
        <div
          className="h-24 bg-cover bg-center border-b border-gray-100"
          style={{ backgroundImage: `url(${prod.banner_url})` }}
          data-testid={`product-banner-${prod.product_id}`}
        />
      )}
      <CardHeader className="pb-4 border-b border-gray-100 bg-gradient-to-br from-indigo-50 to-white">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4">
            {/* Product logo/image — falls back to icon if no URL configured */}
            {(prod.logo_url || prod.image_url) ? (
              <img
                src={prod.logo_url || prod.image_url}
                alt={productDisplayName}
                className="w-14 h-14 rounded-xl object-cover shadow-md flex-shrink-0 bg-white border border-gray-200"
                data-testid={`product-image-${prod.product_id}`}
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "flex"; }}
              />
            ) : null}
            <div
              className={`w-14 h-14 bg-indigo-600 rounded-xl items-center justify-center shadow-md flex-shrink-0 ${
                (prod.logo_url || prod.image_url) ? "hidden" : "flex"
              }`}
            >
              <Package className="w-7 h-7 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-lg font-bold text-gray-900">{groupTitle}</CardTitle>
                {prod.version && (
                  <Badge variant="outline" className="text-xs bg-white">
                    v{prod.version}
                  </Badge>
                )}
              </div>
              <CardDescription className="mt-1 text-gray-600">
                {prod.tv_enabled ? "TradingView access included" : "MetaTrader 5 Expert Advisor"}
              </CardDescription>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-xs">
                  {cycleLabel(prod.billing_cycle)} Plan
                </Badge>
                {allEntries.length > 1 && (
                  <span className="text-xs text-gray-500">
                    · {allEntries.length} plans on this product
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {statusBadge()}
            {days > 0 && days <= 7 && (
              <span className="text-xs text-orange-600 font-medium">
                {days} day{days !== 1 ? "s" : ""} left
              </span>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Variant switcher — only shown when the customer owns >1 plan in this group */}
      {allEntries.length > 1 && (
        <div
          className="px-6 pt-3 pb-0 bg-gradient-to-b from-indigo-50/30 to-white border-b border-gray-100"
          data-testid={`variant-switcher-${prod.display_group}`}
        >
          <div className="flex gap-1 flex-wrap">
            {allEntries.map((entry, idx) => {
              const isSelected = idx === activeIdx;
              const entryActive = isActive(entry.license);
              return (
                <button
                  key={entry.product.product_id}
                  type="button"
                  onClick={() => setActiveIdx(idx)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-t-md border border-b-0 transition-colors ${
                    isSelected
                      ? "bg-white border-indigo-200 text-indigo-900"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                  data-testid={`variant-tab-${entry.product.product_id}`}
                >
                  {cycleLabel(entry.product.billing_cycle)}
                  {entryActive && (
                    <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-green-500 align-middle" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <CardContent className="space-y-5 pt-5">
        {/* License Key */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <Label className="text-xs text-gray-500 uppercase tracking-wider">License Key</Label>
          <div className="flex items-center gap-2 mt-2">
            <code
              className="flex-1 text-xs sm:text-sm font-mono font-bold text-gray-900 bg-white px-3 py-2 rounded border border-gray-200 break-all overflow-hidden"
              data-testid={`license-key-${prod.product_id}`}
            >
              {lic.license_key}
            </code>
            <Button
              variant="outline"
              size="icon"
              className="flex-shrink-0"
              onClick={() => copyToClipboard(lic.license_key)}
              data-testid={`copy-license-key-${prod.product_id}`}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <Label className="text-xs text-gray-500 uppercase tracking-wider">Expiry Date</Label>
            <p className="text-sm font-semibold text-gray-900 mt-1">{formatDate(lic.expiry_date)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <Label className="text-xs text-gray-500 uppercase tracking-wider">Days Remaining</Label>
            <p
              className={`text-sm font-semibold mt-1 ${
                days > 7 ? "text-green-600" : days > 0 ? "text-yellow-600" : "text-red-600"
              }`}
            >
              {days > 0 ? `${days} days` : "Expired"}
            </p>
          </div>
        </div>

        {/* Registered MT5 Accounts (only if the product uses the license engine) */}
        {prod.license_enabled !== false && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Monitor className="w-4 h-4 text-gray-600" />
              <Label className="text-sm font-semibold text-gray-700">
                MT5 Accounts Connected ({accounts.length}/{mt5Cap})
              </Label>
            </div>
            {accounts.length > 0 ? (
              <div className="space-y-2">
                {accounts.map((acc, index) => {
                  const account = typeof acc === "object" ? acc : { account: acc, broker: "Unknown" };
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100 gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-sm font-semibold text-gray-900 truncate">
                          {account.account}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{account.broker || "Unknown Broker"}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                          Active
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDisconnectTarget({ ...account, product_id: prod.product_id })}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                          data-testid={`disconnect-account-btn-${prod.product_id}-${account.account}`}
                          title="Disconnect this account"
                        >
                          <Unplug className="w-4 h-4 sm:mr-1.5" />
                          <span className="hidden sm:inline text-xs">Disconnect</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                No accounts connected yet. Enter this license key in the EA to activate.
              </p>
            )}
            {accounts.length > 0 && accounts.length < mt5Cap && (
              <p className="text-xs text-gray-500 mt-2">
                You can register {mt5Cap - accounts.length} more account(s).
              </p>
            )}
          </div>
        )}

        {/* Platforms Included — shown when >1 platform is available on this
            product (MT5 + TradingView or NinjaTrader). Purely informational. */}
        {(prod.tv_enabled || prod.ninjatrader_enabled) && (
          <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-indigo-600" />
              <Label className="text-xs font-semibold text-indigo-900 uppercase tracking-wider">
                Platforms Included
              </Label>
            </div>
            <div className="flex flex-wrap gap-2" data-testid={`platforms-included-${prod.product_id}`}>
              {prod.license_enabled !== false && (
                <Badge className="bg-white text-gray-800 border border-gray-200 gap-1">
                  <Monitor className="w-3 h-3" /> MetaTrader 5
                </Badge>
              )}
              {prod.tv_enabled && (
                <Badge className="bg-white text-blue-800 border border-blue-200 gap-1">
                  <Sparkles className="w-3 h-3" /> TradingView
                </Badge>
              )}
              {prod.ninjatrader_enabled && (
                <Badge className="bg-white text-orange-800 border border-orange-200 gap-1" data-testid={`nt-badge-${prod.product_id}`}>
                  <Package className="w-3 h-3" /> NinjaTrader 8
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* NinjaTrader devices + download (only if enabled for this product) */}
        {prod.ninjatrader_enabled && (
          <div
            className="bg-orange-50 border border-orange-200 rounded-lg p-4"
            data-testid={`nt-section-${prod.product_id}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-orange-700" />
              <Label className="text-sm font-semibold text-orange-900">
                NinjaTrader 8 Devices ({(lic.nt_activations || []).length}/{ntCap})
              </Label>
              {prod.nt_version && (
                <Badge variant="outline" className="text-xs bg-white">v{prod.nt_version}</Badge>
              )}
            </div>
            {lic.nt_activations && lic.nt_activations.length > 0 ? (
              <div className="space-y-2 mb-3">
                {lic.nt_activations.map((d, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-2.5 rounded-md border border-orange-100 gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-xs font-semibold text-gray-900 truncate" title={d.device_id}>
                        {d.machine_name || d.device_id}
                      </p>
                      {d.last_heartbeat && (
                        <p className="text-xs text-gray-500">
                          Last check-in: {new Date(d.last_heartbeat).toLocaleString("en-IN")}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                      Active
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-orange-800 mb-3">
                No devices connected yet. Install the NinjaTrader 8 strategy and enter this license key to activate.
              </p>
            )}
            {active ? (
              <Button
                onClick={() => onDownloadNt(prod.product_id)}
                disabled={ntDownloadingId === prod.product_id}
                variant="outline"
                className="w-full border-orange-300 text-orange-800 hover:bg-orange-100"
                data-testid={`nt-download-btn-${prod.product_id}`}
              >
                {ntDownloadingId === prod.product_id ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Preparing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download NinjaTrader Strategy
                  </>
                )}
              </Button>
            ) : (
              <p className="text-xs text-orange-800">
                Renew your license to download the NinjaTrader strategy.
              </p>
            )}
          </div>
        )}

        {/* TradingView username (only if enabled for this product) */}
        {prod.tv_enabled && (
          <div
            className={`border rounded-lg p-4 ${
              lic.tv_access_status === "granted"
                ? "bg-green-50 border-green-200"
                : "bg-blue-50 border-blue-200"
            }`}
            data-testid={`tv-section-${prod.product_id}`}
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Sparkles
                className={`w-4 h-4 ${
                  lic.tv_access_status === "granted" ? "text-green-600" : "text-blue-600"
                }`}
              />
              <Label
                className={`text-sm font-semibold ${
                  lic.tv_access_status === "granted" ? "text-green-900" : "text-blue-900"
                }`}
              >
                TradingView Access
              </Label>
              {lic.tv_access_status === "granted" && (
                <Badge className="bg-green-100 text-green-800 border-green-300 text-xs" data-testid={`tv-granted-badge-${prod.product_id}`}>
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Granted
                </Badge>
              )}
              {lic.tv_username && lic.tv_access_status !== "granted" && (
                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs" data-testid={`tv-pending-badge-${prod.product_id}`}>
                  Pending review
                </Badge>
              )}
            </div>

            {lic.tv_access_status === "granted" ? (
              <div className="text-sm text-green-800">
                <p>
                  Access has been provisioned for <b>{lic.tv_username}</b>. Open TradingView and refresh
                  your indicators — the invite-only script should now be available.
                </p>
              </div>
            ) : lic.tv_username ? (
              <>
                <p className="text-sm text-blue-800 mb-3">
                  <b>TradingView Username Submitted:</b>{" "}
                  <span className="font-mono">{lic.tv_username}</span>
                  <br />
                  Our team will review your request and grant access within 24 hours. You&apos;ll see
                  the status change to <b>Granted</b> here once it&apos;s live.
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Update username if incorrect"
                    value={tvInput}
                    onChange={(e) => setTvInput(e.target.value)}
                    className="bg-white flex-1"
                    maxLength={60}
                    data-testid={`tv-username-input-${prod.product_id}`}
                  />
                  <Button
                    onClick={() => onSubmitTv(prod.product_id, tvInput)}
                    disabled={!tvInput.trim() || tvSubmittingId === prod.product_id || tvInput.trim() === (lic.tv_username || "")}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    data-testid={`tv-username-submit-${prod.product_id}`}
                  >
                    {tvSubmittingId === prod.product_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Update</span>
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-blue-800 mb-3">
                  Share your TradingView username so our team can grant access to the indicator.
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder="your-tradingview-username"
                    value={tvInput}
                    onChange={(e) => setTvInput(e.target.value)}
                    className="bg-white flex-1"
                    maxLength={60}
                    data-testid={`tv-username-input-${prod.product_id}`}
                  />
                  <Button
                    onClick={() => onSubmitTv(prod.product_id, tvInput)}
                    disabled={!tvInput.trim() || tvSubmittingId === prod.product_id}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid={`tv-username-submit-${prod.product_id}`}
                  >
                    {tvSubmittingId === prod.product_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Submit</span>
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Per-product subscription status (Feb 2026) — replaces the old
            global Billing card for renewal info. Global card now only shows
            payment history. */}
        {(sub.razorpay_subscription_id || active) && (
          <div
            className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${
              sub.auto_renewal_cancelled
                ? "bg-orange-50 border-orange-200"
                : sub.razorpay_subscription_id
                ? "bg-green-50 border-green-200"
                : "bg-blue-50 border-blue-200"
            }`}
            data-testid={`subscription-banner-${prod.product_id}`}
          >
            {sub.auto_renewal_cancelled ? (
              <XCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
            ) : (
              <ShieldCheck className={`w-4 h-4 flex-shrink-0 mt-0.5 ${sub.razorpay_subscription_id ? "text-green-600" : "text-blue-600"}`} />
            )}
            <div className="flex-1 min-w-0">
              {sub.auto_renewal_cancelled ? (
                <>
                  <p className="font-semibold text-orange-900">Auto-renewal cancelled</p>
                  <p className="text-orange-800 text-xs mt-0.5">
                    Access continues until <b>{formatDate(lic.expiry_date)}</b>. Renew manually to keep using it.
                  </p>
                </>
              ) : sub.razorpay_subscription_id ? (
                <>
                  <p className="font-semibold text-green-900">Auto-renewal active</p>
                  <p className="text-green-800 text-xs mt-0.5">
                    Next charge <b>{formatINR(prod.price_paise)}</b> on <b>{formatDate(sub.next_renewal_estimate)}</b>.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-blue-900">Manual renewal</p>
                  <p className="text-blue-800 text-xs mt-0.5">
                    Pay <b>{formatINR(prod.price_paise)}</b> every {prod.cycle_days} days to extend.
                  </p>
                </>
              )}
            </div>
            {sub.can_self_cancel && (
              <Button
                variant="ghost"
                size="sm"
                className="text-orange-700 hover:bg-orange-100 h-7"
                onClick={() => setShowCancelDialog(true)}
                disabled={cancellingId === prod.product_id}
                data-testid={`cancel-subscription-btn-${prod.product_id}`}
              >
                {cancellingId === prod.product_id ? "..." : "Cancel"}
              </Button>
            )}
            {!sub.can_self_cancel && !sub.auto_renewal_cancelled && active && (
              <Button
                variant="ghost"
                size="sm"
                className="text-green-700 hover:bg-green-100 h-7"
                onClick={() => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer")}
                data-testid={`contact-support-cancel-${prod.product_id}`}
                title="No auto-renewal to cancel — chat with us to stop future manual renewals"
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1" />
                Contact Support to Cancel
              </Button>
            )}
          </div>
        )}

        {/* Actions: Download + Renew */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          {active ? (
            <Button
              onClick={() => onDownload(prod.product_id)}
              disabled={downloadingId === prod.product_id}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid={`download-btn-${prod.product_id}`}
            >
              {downloadingId === prod.product_id ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download MT5 EA
                </>
              )}
            </Button>
          ) : (
            <div className="flex-1 flex flex-col gap-1">
              <Button
                onClick={() => onRenew(prod, lic)}
                disabled={renewingId === prod.product_id}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                data-testid={`renew-btn-${prod.product_id}`}
              >
                {renewingId === prod.product_id ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Opening…
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Renew Subscription
                  </>
                )}
              </Button>
              <p className="text-xs text-orange-600 text-center">
                {lic.status === "expired" ? "License expired" : "License not active"}
              </p>
            </div>
          )}

          {active && (
            <Button
              variant="outline"
              onClick={() => onRenew(prod, lic)}
              disabled={renewingId === prod.product_id}
              className="flex-1"
              data-testid={`renew-secondary-btn-${prod.product_id}`}
            >
              {renewingId === prod.product_id ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Opening…
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Renew / Upgrade
                </>
              )}
            </Button>
          )}
        </div>

        {/* Add More Accounts — ORB-family only, active licenses only.
            Mid-cycle seat top-up with prorated pricing. */}
        {active && (prod.display_group || "").toLowerCase() === "monk-algo-orb" && (
          <Button
            variant="outline"
            onClick={() => onAddSeats(prod, lic)}
            className="w-full border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            data-testid={`add-seats-btn-${prod.product_id}`}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Add More Accounts
          </Button>
        )}

        {active && (
          <p className="text-xs text-gray-500 text-center">
            Download opens Google Drive in a new tab. Your active license and registered accounts govern usage.
          </p>
        )}
      </CardContent>

      {/* Disconnect confirmation */}
      <AlertDialog
        open={!!disconnectTarget}
        onOpenChange={(open) => { if (!open) setDisconnectTarget(null); }}
      >
        <AlertDialogContent data-testid={`disconnect-dialog-${prod.product_id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect this account?</AlertDialogTitle>
            <AlertDialogDescription>
              Account <b className="font-mono">{disconnectTarget?.account}</b>
              {disconnectTarget?.broker ? <> on <b>{disconnectTarget.broker}</b></> : null} will be
              removed from your license for <b>{groupTitle}</b>. The EA will stop working on this
              account on its next validation. The slot will become available so you can register a
              new account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`disconnect-cancel-${prod.product_id}`}>
              Keep account
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                await onDisconnect(disconnectTarget.account, prod.product_id);
                setDisconnectTarget(null);
              }}
              data-testid={`disconnect-confirm-${prod.product_id}`}
            >
              Yes, disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Per-product Cancel-subscription confirmation */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent data-testid={`cancel-dialog-${prod.product_id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel auto-renewal for {groupTitle}?</AlertDialogTitle>
            <AlertDialogDescription>
              Your license for <b>{groupTitle}</b> stays active until <b>{formatDate(lic.expiry_date)}</b>.
              After that, no further charges will be made and the EA will stop trading unless you renew.
              You can resubscribe anytime. Your other products are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancellingId === prod.product_id} data-testid={`cancel-dialog-keep-${prod.product_id}`}>
              Keep my subscription
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={cancellingId === prod.product_id}
              onClick={async () => {
                await onCancel(prod.product_id);
                setShowCancelDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid={`cancel-dialog-confirm-${prod.product_id}`}
            >
              {cancellingId === prod.product_id ? "Cancelling..." : "Yes, cancel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

/* ---------- Main Client Portal ---------- */
export default function ClientPortal() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [clientData, setClientData] = useState(null);
  const [licenses, setLicenses] = useState([]); // [{license, product}, ...]
  const [payments, setPayments] = useState([]);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [ntDownloadingId, setNtDownloadingId] = useState(null);
  const [tvSubmittingId, setTvSubmittingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    const savedToken = sessionStorage.getItem(CLIENT_TOKEN_KEY);
    const savedData = sessionStorage.getItem(CLIENT_DATA_KEY);
    if (savedToken && savedData) {
      try {
        const data = JSON.parse(savedData);
        setClientData(data.client);
        setIsLoggedIn(true);
        loadAll();
      } catch (e) {
        sessionStorage.removeItem(CLIENT_TOKEN_KEY);
        sessionStorage.removeItem(CLIENT_DATA_KEY);
      }
    }
  }, []);

  const authHeaders = () => {
    const token = sessionStorage.getItem(CLIENT_TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadMyProducts = async () => {
    try {
      const res = await axios.get(`${API}/client/my-products`, { headers: authHeaders() });
      setLicenses(res.data?.licenses || []);
    } catch (err) {
      console.error("my-products fetch failed:", err);
    }
  };

  const loadBilling = async () => {
    setBillingLoading(true);
    try {
      const res = await axios.get(`${API}/client/billing`, { headers: authHeaders() });
      setBilling(res.data);
      if (res.data?.payments) setPayments(res.data.payments);
    } catch (err) {
      console.error("billing fetch failed:", err);
    } finally {
      setBillingLoading(false);
    }
  };

  const loadAll = async () => {
    await loadMyProducts();
    await loadBilling();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Please enter email and password");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/client/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      if (res.data.success) {
        sessionStorage.setItem(CLIENT_TOKEN_KEY, res.data.token);
        sessionStorage.setItem(
          CLIENT_DATA_KEY,
          JSON.stringify({ client: res.data.client, license: res.data.license })
        );
        setClientData(res.data.client);
        setIsLoggedIn(true);
        await loadAll();
        toast.success("Login successful");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(CLIENT_TOKEN_KEY);
    sessionStorage.removeItem(CLIENT_DATA_KEY);
    setIsLoggedIn(false);
    setClientData(null);
    setLicenses([]);
    setPayments([]);
    setBilling(null);
    setLoginEmail("");
    setLoginPassword("");
  };

  const handleDownload = async (productId) => {
    setDownloadingId(productId);
    try {
      const res = await axios.get(`${API}/client/download-ea/${productId}`, {
        headers: authHeaders(),
      });
      const url = res.data?.download_url;
      if (!url) {
        toast.error("Download link is not available. Please contact support.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Opening Google Drive…");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) toast.error("License not active. Please renew to download.");
      else if (status === 503) toast.error(detail || "Download link not configured yet.");
      else if (status === 404) toast.error("No license for this product.");
      else toast.error(detail || "Download failed. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadNt = async (productId) => {
    setNtDownloadingId(productId);
    try {
      const res = await axios.get(`${API}/client/download-ninjatrader/${productId}`, {
        headers: authHeaders(),
      });
      const url = res.data?.download_url;
      if (!url) {
        toast.error("NinjaTrader download link is not available. Please contact support.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Opening NinjaTrader download…");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) toast.error("License not active. Please renew to download.");
      else if (status === 503) toast.error(detail || "NinjaTrader download not configured yet.");
      else if (status === 404) toast.error(detail || "NinjaTrader access is not included in this product.");
      else toast.error(detail || "Download failed. Please try again.");
    } finally {
      setNtDownloadingId(null);
    }
  };

  const handleSubmitTv = async (productId, tvUsername) => {
    setTvSubmittingId(productId);
    try {
      const res = await axios.post(
        `${API}/client/tradingview-username`,
        { product_id: productId, tv_username: tvUsername },
        { headers: authHeaders() }
      );
      toast.success(res.data?.message || "TradingView username submitted");
      // Optimistically update local state
      setLicenses((prev) =>
        prev.map((entry) =>
          entry.product.product_id === productId
            ? {
                ...entry,
                license: { ...entry.license, tv_username: tvUsername, tv_access_status: "pending" },
              }
            : entry
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit TradingView username");
    } finally {
      setTvSubmittingId(null);
    }
  };

  const handleDisconnect = async (accountNumber, productId) => {
    try {
      const res = await axios.delete(
        `${API}/client/accounts/${encodeURIComponent(accountNumber)}?product_id=${encodeURIComponent(productId)}`,
        { headers: authHeaders() }
      );
      toast.success(res.data?.message || "Account disconnected");
      await loadMyProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't disconnect that account.");
    }
  };

  const handleCancelForProduct = async (productId) => {
    setCancellingId(productId);
    try {
      const res = await axios.post(
        `${API}/client/cancel-subscription`,
        { product_id: productId },
        { headers: authHeaders() }
      );
      const activeUntil = res.data?.active_until;
      const niceDate = activeUntil
        ? new Date(activeUntil).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
          })
        : null;
      toast.success(
        niceDate
          ? `Subscription cancelled. Your license stays active until ${niceDate}.`
          : "Subscription cancelled."
      );
      // Reload data
      await loadMyProducts();
      await loadBilling();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402 && typeof detail === "object" && detail?.code === "NO_SUBSCRIPTION_ID") {
        toast.error(detail.message || "Please contact us on WhatsApp.");
        setTimeout(() => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer"), 800);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Couldn't cancel right now.");
    } finally {
      setCancellingId(null);
    }
  };

  const [renewingId, setRenewingId] = useState(null);
  // Feb 2026: dialog state for the two new subscription actions.
  // Each holds `{ product, license }` when open, or null when closed.
  const [renewDialog, setRenewDialog] = useState(null);
  const [addSeatsDialog, setAddSeatsDialog] = useState(null);

  const openRenewDialog = (product, license) => {
    // Non-tiered products (Breakout EA etc.) don't support custom quantities —
    // fall back to the legacy one-click renew flow.
    const displayGroup = (product?.display_group || "").toLowerCase();
    if (displayGroup !== "monk-algo-orb") {
      handleRenew(product);
      return;
    }
    setRenewDialog({ product, license });
  };

  const openAddSeatsDialog = (product, license) => {
    setAddSeatsDialog({ product, license });
  };

  const handleRenew = async (product) => {
    if (!product?.product_id) return;
    setRenewingId(product.product_id);
    try {
      const res = await axios.post(
        `${API}/client/renew`,
        { product_id: product.product_id },
        { headers: authHeaders() },
      );
      const url = res.data?.short_url;
      if (!url) throw new Error("No checkout URL");
      // Redirect the same tab into Razorpay Checkout — after payment
      // Razorpay's callback_url brings the customer back to /client.
      window.location.href = url;
    } catch (err) {
      // Graceful fallback: try the admin-configured static Payment Link if
      // present, otherwise send them to WhatsApp support. Never surface
      // Razorpay internals.
      const detail = err.response?.data?.detail;
      const staticLink = product?.razorpay_link;
      if (staticLink) {
        toast.warning(
          typeof detail === "string" && detail.length < 140
            ? detail
            : "Opening a fallback payment link…",
        );
        window.open(staticLink, "_blank", "noopener,noreferrer");
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Couldn't start renewal right now. Please try again or contact support on WhatsApp.",
        );
        window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer");
      }
    } finally {
      setRenewingId(null);
    }
  };


  /* ---------- Grouping ---------- */
  // Group by display_group. First entry becomes the "primary" (prefer active).
  const groupedProducts = (() => {
    const map = new Map();
    for (const entry of licenses) {
      const grp = entry.product?.display_group || entry.product?.product_id;
      if (!map.has(grp)) map.set(grp, { display_group: grp, entries: [] });
      map.get(grp).entries.push(entry);
    }
    // Choose primary = active first, then latest expiry.
    // The whole entry (license + product + subscription) is preserved.
    const groups = [];
    for (const { display_group, entries } of map.values()) {
      const sorted = [...entries].sort((a, b) => {
        const aActive = isActive(a.license) ? 1 : 0;
        const bActive = isActive(b.license) ? 1 : 0;
        if (aActive !== bActive) return bActive - aActive;
        return new Date(b.license.expiry_date) - new Date(a.license.expiry_date);
      });
      groups.push({
        display_group,
        primary: sorted[0],
        variants: sorted.slice(1),
      });
    }
    return groups;
  })();

  /* ---------- Login screen ---------- */
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border border-gray-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Client Portal</CardTitle>
            <CardDescription className="text-gray-500">
              Login to view your license details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="pl-10"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="pl-10"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ---------- Dashboard ---------- */
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Key className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Client Portal</h1>
              <p className="text-xs text-gray-500">{clientData?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Section: My Products (per-product cards) */}
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-gray-700" />
          <h2 className="text-lg font-semibold text-gray-900">My Products</h2>
        </div>

        {groupedProducts.length === 0 ? (
          <Card className="bg-white border border-gray-200">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No products found on this account.</p>
              <p className="text-sm text-gray-400 mt-2">Contact support if you believe this is an error.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="my-products-list">
            {groupedProducts.map((group) => (
              <ProductCard
                key={group.display_group}
                group={group}
                onDisconnect={handleDisconnect}
                onDownload={handleDownload}
                onDownloadNt={handleDownloadNt}
                onSubmitTv={handleSubmitTv}
                onRenew={openRenewDialog}
                onCancel={handleCancelForProduct}
                onAddSeats={openAddSeatsDialog}
                downloadingId={downloadingId}
                ntDownloadingId={ntDownloadingId}
                tvSubmittingId={tvSubmittingId}
                cancellingId={cancellingId}
                renewingId={renewingId}
              />
            ))}
          </div>
        )}

        {/* Payment History (global, product-agnostic view of ALL paid charges) */}
        <Card className="bg-white border border-gray-200" data-testid="billing-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment History
            </CardTitle>
            <CardDescription className="text-gray-500">
              All successful payments across your products. Manage renewals from each product card above.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadBilling}
                disabled={billingLoading}
                data-testid="billing-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${billingLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer")}
                className="border-green-300 text-green-700 hover:bg-green-50"
                data-testid="billing-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat on WhatsApp
              </Button>
            </div>

            {payments.length === 0 ? (
              <p className="text-center text-gray-500 py-6 text-sm">No payments yet</p>
            ) : (
              <div className="space-y-2">
                {payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                    data-testid="payment-history-row"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {payment.plan_name || "License Payment"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(payment.created_at).toLocaleDateString("en-IN", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <p className="font-semibold text-gray-900 text-sm">
                        ₹{(payment.amount / 100).toLocaleString("en-IN")}
                      </p>
                      <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">PAID</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Feb 2026: Renew / Upgrade + Add Seats dialogs */}
      <RenewPlanDialog
        open={!!renewDialog}
        onOpenChange={(v) => !v && setRenewDialog(null)}
        product={renewDialog?.product}
        license={renewDialog?.license}
        authHeaders={authHeaders}
      />
      <AddSeatsDialog
        open={!!addSeatsDialog}
        onOpenChange={(v) => !v && setAddSeatsDialog(null)}
        product={addSeatsDialog?.product}
        license={addSeatsDialog?.license}
        authHeaders={authHeaders}
      />
    </div>
  );
}
